import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

import { DateInputsModule, DatePickerModule } from "@progress/kendo-angular-dateinputs";
import { DropDownsModule } from "@progress/kendo-angular-dropdowns";
import { LabelModule } from "@progress/kendo-angular-label";

import { DateRange } from 'src/app/modules/fwcore/models';

@Component({
  selector: 'daterange-criteria',
  templateUrl: './daterange-criteria.component.html',
  styleUrls: ['./daterange-criteria.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeCriteriaComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [DateInputsModule, DatePickerModule, DropDownsModule,
    LabelModule, ReactiveFormsModule]
})

export class DateRangeCriteriaComponent
  implements ControlValueAccessor {
  /*-------------------------- Input / Outputs --------------------------------*/
  @Output() onValueChange = new EventEmitter<DateRange>();

  public get hasValue(): boolean { return this._hasValue; }

  /* ------------------------------- Protected ------------------------------- */
  protected dateRangeTypeValue!: string;
  protected isCustomValue: boolean = false;
  protected startDate: Date = new Date();
  protected endDate: Date = new Date();
  protected _onTouched: Function = () => {};
  protected _onChange: undefined | ((value: DateRange) => void);
  protected datePickersDisable: boolean = true;

  protected _rangeList: Array<string> = [
    "Year to date", "This year", "Last month", "Year to end last month",
    "12 months to end last month", "This month", "Next month",
    "Last week", "This week", "Next week",
    "This year Q1", "This year Q2", "This year Q3", "This year Q4",
    "Next year Q1", "Next year",
    "Last year Q1", "Last year Q2", "Last year Q3", "Last year Q4", "Last year",
    "All", "Today", "Custom"
  ];

  /* ------------------------------- Setup ------------------------------- */
  public _hasValue: boolean = false;
  constructor (private readonly _cdr: ChangeDetectorRef) {
  }

  /* ------------------------------- Properties -------------------------------  */

  public get value(): DateRange {
    let result = new DateRange();

    result.dateRangeType = this.dateRangeTypeValue;

    result.startDate = this.startDate;
    result.startDate.setHours(0, 0, 0, 0);

    result.endDate = this.endDate;
    result.endDate.setHours(23, 59, 59, 999);

    return result;
  }

  public set value(value: DateRange) {
    if ((value?.dateRangeType)
      && (this._rangeList.indexOf(value.dateRangeType) != -1)) {
      this.setDateRange(value.dateRangeType);
    }
    if (this.isCustomValue) {
      this.startDate = value.startDate;
      this.endDate = value.endDate;
    }
  }

  /* ------------------------------- Events -------------------------------  */

  private emitOnChange() {
    this.onValueChange.emit(this.value);
    if (this._onChange != undefined) {
      this._onChange(this.value);
    }
  }

  protected dateRangeTypeValueChange(value: any): void {
    this.setDateRange(value);
    this.setControlVisibility();
    this.emitOnChange();
  }

  protected endDateChange(value: Date): void {
    this.value.endDate = value;
    this._hasValue = true;
    this.emitOnChange();
  }

  protected startDateChange(value: Date): void {
    this.startDate = value;
    this._hasValue = true;
    this.emitOnChange();
  }

  /* -------------------------------  ControlValueAccessor ------------------------------- */

  writeValue(value: DateRange) {
    this.value = value;
    this._cdr.markForCheck();
  }

  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Can't be removed as this is required to satisfy the interface
  }
  /* -------------------------------  Private functions ------------------------------- */

  // enable / disable date pickers
  private setControlVisibility(): void {
    // disabled unless custom value
    this.datePickersDisable = !this.isCustomValue;
  }

  private setDateRange(dateRangeType: any): void {
    this.dateRangeTypeValue = dateRangeType;

    let from = new Date();
    from.setHours(0, 0, 0, 0);

    let to = new Date(from);

    let lastMonth = this.addMonths(from, -1);

    let dayDiff = 0;

    switch (dateRangeType) {
      case "All":
        from = new Date(1980, 0, 1);
        to = new Date(2080, 0, 1);
        break;
      case "Year to date":
        from = new Date(from.getFullYear(), 0, 1);
        break;
      case "Year to end last month":
        to = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), this.getDaysInMonth(lastMonth));
        from = new Date(to.getFullYear(), 0, 1);
        break;
      case "12 months to end last month":
        to = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), this.getDaysInMonth(lastMonth));
        lastMonth = this.addMonths(lastMonth, -11);
        from = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
        break;
      case "Last year":
        from = new Date(this.addYears(from, -1).getFullYear(), 0, 1);
        to = new Date(from.getFullYear(), 11, 31);
        break;
      case "Last year Q1":
        from = new Date(this.addYears(from, -1).getFullYear(), 0, 1);
        to = new Date(from.getFullYear(), 2, 31);
        break;
      case "Last year Q2":
        from = new Date(this.addYears(from, -1).getFullYear(), 3, 1);
        to = new Date(from.getFullYear(), 5, 30);
        break;
      case "Last year Q3":
        from = new Date(this.addYears(from, -1).getFullYear(), 6, 1);
        to = new Date(from.getFullYear(), 8, 30);
        break;
      case "Last year Q4":
        from = new Date(this.addYears(from, -1).getFullYear(), 9, 1);
        to = new Date(from.getFullYear(), 11, 31);
        break;
      case "This year":
        from = new Date(from.getFullYear(), 0, 1);
        to = new Date(from.getFullYear(), 11, 31);
        break;
      case "This year Q1":
        from = new Date(from.getFullYear(), 0, 1);
        to = new Date(from.getFullYear(), 2, 31);
        break;
      case "This year Q2":
        from = new Date(from.getFullYear(), 3, 1);
        to = new Date(from.getFullYear(), 5, 30);
        break;
      case "This year Q3":
        from = new Date(from.getFullYear(), 6, 1);
        to = new Date(from.getFullYear(), 8, 30);
        break;
      case "This year Q4":
        from = new Date(from.getFullYear(), 9, 1);
        to = new Date(from.getFullYear(), 11, 31);
        break;
      case "Next year":
        from = new Date(this.addYears(from, 1).getFullYear(), 0, 1);
        to = new Date(from.getFullYear(), 11, 31);
        break;
      case "Next year Q1":
        from = new Date(this.addYears(from, 1).getFullYear(), 0, 1);
        to = new Date(from.getFullYear(), 2, 31);
        break;
      case "Last month":
        from = lastMonth;
        from.setDate(1);
        to = new Date(from);
        to.setDate(this.getDaysInMonth(to));
        break;
      case "This month":
        from.setDate(1);
        to.setDate(this.getDaysInMonth(to));
        break;
      case "Next month":
        from = this.addMonths(from, 1);
        from.setDate(1);
        to = new Date(from);
        to.setDate(this.getDaysInMonth(to));
        break;
      case "Last week":
        dayDiff = from.getDay() + 7;
        from = this.addDays(from, -dayDiff);
        to = this.addDays(from, 6);
        break;
      case "This week":
        dayDiff = from.getDay();
        from = this.addDays(from, -dayDiff);
        to = this.addDays(from, 6);
        break;
      case "Next week":
        dayDiff = 7 - from.getDay();
        from = this.addDays(from, dayDiff);
        to = this.addDays(new Date(from), 6);
        break;
      case "Today":
        break;
      case "Custom":
        this.isCustomValue = true;
        this._hasValue = true;
        return;
      default:
        this.value = new DateRange();
        this._hasValue = false;
        return;
    }

    this.startDate = from;
    this.endDate = to;
    this._hasValue = true;
  }

  /* ------------------------------- Date functions ------------------------------- */
  private isLeapYear(year: number): boolean {
    return ((year % 4 === 0) && (year % 100 !== 0))
      || (year % 400 === 0);
  }

  private getDaysInMonth(dt: Date): number {
    if (this.isLeapYear(dt.getFullYear())) {
      return [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][dt.getMonth()];
    } else {
      return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][dt.getMonth()];
    }
  }

  private addDays(dt: Date, days: number): Date {
    let result = new Date(dt);
    result.setDate(result.getDate() + days);
    return result;
  }

  private addMonths(value: Date, months: number): Date {
    let result = new Date(value);
    const n = value.getDate();
    result.setMonth(value.getMonth() + months);
    result.setDate(Math.min(n, this.getDaysInMonth(result)));
    return result;
  }

  private addYears(dt: Date, years: number): Date {
    let result = new Date(dt);
    const n = dt.getDate();
    result.setDate(1);
    result.setFullYear(dt.getFullYear() + years);
    result.setDate(Math.min(n, this.getDaysInMonth(dt)));
    return result;
  }
}
